import { tr } from "./i18n.js";
/**
 * The writer's source limit, shared by every "add a source" surface.
 *
 * `generate_article` sends only the first MAX_WRITER_SOURCES sources to
 * the writer (backend/source_clip.py → MAX_WRITER_SOURCES) and drops
 * the rest. Before 2026-09-24 nothing in the UI said so: Find more
 * sources, the manual "Add more sources" box, and the Custom Article
 * URL list all accepted any number, and the editor only learned that
 * sources 9+ were ignored from the amber notice on the finished
 * article. This module lets each surface show "N of 8" and refuse the
 * ninth.
 *
 * Some slots are taken before any URL is added:
 *   - Custom Article: the editor's brief (headline or notes) takes one
 *     slot, and each attached document takes one. They go FIRST, so
 *     URLs get whatever is left (routes/custom_articles.py).
 *   - Telegram tip: the tip's own text always goes in as the editor's
 *     brief, so URLs get 7 (routes/telegram.py).
 *   - Research idea: nothing reserved — only checked URLs count.
 *
 * Keep MAX_WRITER_SOURCES equal to the backend constant.
 * backend/tests/test_source_limit_parity.py fails if they drift.
 *
 * Pure and framework-free — unit-tested with node:
 *     node --test frontend/scripts/sourceLimit.test.mjs
 */

export const MAX_WRITER_SOURCES = 8;

/** Unique, non-empty URLs, first occurrence wins (the order the backend sends). */
export function uniqueUrls(urls) {
  const seen = new Set();
  const out = [];
  for (const raw of urls || []) {
    const u = (raw || "").trim();
    if (!u || seen.has(u)) continue;
    seen.add(u);
    out.push(u);
  }
  return out;
}

/** How many URLs fit once `reserved` slots are taken by editor material. */
export function urlCapacity(reserved = 0, max = MAX_WRITER_SOURCES) {
  return Math.max(0, max - Math.max(0, reserved | 0));
}

/** Free slots left for URLs. */
export function slotsLeft(urlCount, reserved = 0, max = MAX_WRITER_SOURCES) {
  return Math.max(0, urlCapacity(reserved, max) - Math.max(0, urlCount | 0));
}

/**
 * Split `candidates` into the ones that fit and the ones that don't,
 * given `current` URLs already counted. Candidates already in
 * `current` (or repeated) are dropped: they neither use a slot nor
 * count as rejected.
 */
export function takeWithinLimit(candidates, current, reserved = 0, max = MAX_WRITER_SOURCES) {
  const have = new Set(uniqueUrls(current));
  let free = slotsLeft(have.size, reserved, max);
  const accepted = [];
  const rejected = [];
  for (const u of uniqueUrls(candidates)) {
    if (have.has(u)) continue;
    if (free > 0) {
      accepted.push(u);
      have.add(u);
      free -= 1;
    } else {
      rejected.push(u);
    }
  }
  return { accepted, rejected };
}

/**
 * URLs in `ordered` that fall past the cut-off and will NOT reach the
 * writer. Used to flag rows when the list is already over the limit
 * (an idea that arrived with more than 8, or notes/docs added after
 * the URLs on the Custom Article page).
 */
export function overflowUrls(ordered, reserved = 0, max = MAX_WRITER_SOURCES) {
  return uniqueUrls(ordered).slice(urlCapacity(reserved, max));
}

/** Custom Article: slots taken by the editor's brief + attached documents. */
export function customArticleReserved({ headline = "", notes = "", docCount = 0 } = {}) {
  const brief = (headline || "").trim() || (notes || "").trim() ? 1 : 0;
  return brief + Math.max(0, docCount | 0);
}

/** Telegram tip: the tip text is always sent as the editor's brief. */
export const TELEGRAM_TIP_RESERVED = 1;

/**
 * Plain-English note on reserved slots, or "" if nothing is reserved.
 * e.g. "2 of the 8 go to your notes and 1 attached document"
 */
export function reservedLabel({ brief = false, docCount = 0, briefName = tr("your notes") } = {}, max = MAX_WRITER_SOURCES) {
  const parts = [];
  if (brief) parts.push(briefName);
  if (docCount > 0) parts.push(tr("{docCount} attached document{s}", { docCount, s: docCount === 1 ? "" : "s" }));
  if (parts.length === 0) return "";
  const n = (brief ? 1 : 0) + Math.max(0, docCount | 0);
  return tr("{n} of the {max} go{s} to {list}", { n, max, s: n === 1 ? tr("es") : "", list: parts.join(" and ") });
}

/** Toast text when an add is refused because the list is full. */
export function fullMessage(max = MAX_WRITER_SOURCES) {
  return tr("The writer only takes {max} sources — remove or uncheck one to add another", { max });
}

/** Toast text after a partial "Add all". */
export function partialAddMessage(added, left, max = MAX_WRITER_SOURCES) {
  return tr("Added {added} — that fills the {max}-source limit. {left} not added.", { added, max, left });
}